import { useApi } from '@/api';
import { ChartTimeFilter, ExpandableKpiCard } from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  Flex,
  formatPercentage,
  KpiCard,
  KpiSkeleton,
  LineChart,
  PercentageValue,
  useIsMobile
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo, useState } from 'react';

type SLLIndexResponse = {
  real_apy: string;
  real_apy_change: string | null;
  historic: SimpleChartData[];
};

interface APYKpiChartProps {
  network: string;
}

export default function APYKpi({ network }: APYKpiChartProps) {
  const isMobile = useIsMobile();
  const color = useSparkColor();
  const [chartDaysAgo, setChartDaysAgo] = useState('90');
  const [data, error] = useApi<SLLIndexResponse>({
    url: `/sparkstar/sll/networks/${network}/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const datasets = useMemo(
    () => [
      {
        xField: 'date',
        yField: 'real_apy',
        borderColor: color
      }
    ],
    [color]
  );

  const customConfig = useMemo(
    () => ({
      options: {
        aspectRatio: 2.5
      }
    }),
    []
  );

  const chartData = useChartFillDatasetsDates(
    data?.historic,
    datasets,
    chartDaysAgo
  );
  const valueFormatter = useCallback((value: string | number) => {
    return <PercentageValue value={value} />;
  }, []);

  const yScaleValueFormatter = useCallback((value: string | number) => {
    return formatPercentage(Number(value));
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <KpiSkeleton />;
  }

  return (
    <ExpandableKpiCard
      text={<Flex gap="3xs">APY</Flex>}
      hoverBorderColor={color}
      defaultShowChart={!isMobile}
      kpiCard={
        <KpiCard
          data={{
            real_apy: data['real_apy'],
            real_apy_change: data['real_apy_change']
          }}
          field="real_apy"
          options={{
            percentage: true,
            variants: {
              kpiCardValue: 'mainValue'
            }
          }}
          variant="nestedAsChild"
        />
      }>
      <LineChart
        data={chartData}
        datasets={datasets}
        valueFormatter={valueFormatter}
        yScaleValueFormatter={yScaleValueFormatter}
        showValuesInTooltip={true}
        config={customConfig}
        tooltip={{
          valueFormatter: (value) => {
            return formatPercentage(Number(value));
          },
          labelFormatter: () => 'APY',
          displayColors: false
        }}
        timeFilter={
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        }
      />
    </ExpandableKpiCard>
  );
}
